import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { publicConfig } from 'config'
import {
  factLine,
  freshCard,
  loadMenuStats,
  menuIsFresh,
  peekMenuStats,
  rankTile,
  scheduleIdleMenuWarm,
  readersHead,
  readingTile,
  sparkBars,
  statsSeen,
  upNext,
  type MenuStatsResponse,
  type Tile,
} from 'lib/community/menu-stats'
import type { CommunityStanding } from 'lib/community/types'
import OwnPicker from './OwnPicker'
import base from './community.module.css'
import styles from './AccountMenuIdentity.module.css'

/** `loading` = the menu opened and the read is on its way; null = no glance (never opened, or the
 *  read failed: 503 before DealHunt's db:push, 404, a blip) — the v1 head alone. */
export type MenuStatsState = MenuStatsResponse | 'loading' | null

/**
 * The glance's data: shown when the menu opens (`opened` counts opens; 0 = never), from the
 * five-minute per-handle promise (`loadMenuStats`) — usually already settled by the header's
 * warm (idle / avatar hover, `useMenuStatsWarm`), in which case the first open render reads it
 * synchronously (`peekMenuStats`) and the placeholder never paints. A re-open keeps the last
 * answer on screen while the cached promise answers again.
 */
export function useMenuStats(handle: string | null, opened: number): MenuStatsState {
  const trailingSlash = Boolean(publicConfig('useTrailingSlash'))
  const [answer, setAnswer] = useState<{ handle: string; value: MenuStatsResponse | null } | null>(
    null
  )
  useEffect(() => {
    if (!handle || opened === 0) {
      return undefined
    }
    let live = true
    void loadMenuStats(handle, trailingSlash).then(value => {
      if (live) {
        setAnswer({ handle, value })
      }
    })
    return () => {
      live = false
    }
  }, [handle, opened, trailingSlash])
  if (!handle || opened === 0) {
    return null
  }
  if (answer && answer.handle === handle) {
    return answer.value
  }
  return peekMenuStats(handle) ?? 'loading'
}

/**
 * The header's early load of the reader's own glance (Amendment C.3): on idle when a hub handle
 * is known (remembered from the last visit) and the page is under /community — `path` is the
 * router's, re-scheduled on every navigation and read when the idle callback runs, so a client
 * navigation into /community warms too — and `warm()` for the avatar button's hover/focus, on
 * every page. Both go through `loadMenuStats`' five-minute promise, so however often they fire it
 * is one read; `/me` is not touched (it stays on open, Constraint a8b375cd).
 */
export function useMenuStatsWarm(handle: string | null, path: string): () => void {
  const trailingSlash = Boolean(publicConfig('useTrailingSlash'))
  const pathRef = useRef(path)
  pathRef.current = path
  useEffect(() => {
    if (!handle) {
      return undefined
    }
    return scheduleIdleMenuWarm(handle, trailingSlash, () => pathRef.current)
  }, [handle, trailingSlash, path])
  return useCallback(() => {
    if (handle) {
      void loadMenuStats(handle, trailingSlash)
    }
  }, [handle, trailingSlash])
}

const MenuTile: React.VFC<{ tile: Tile }> = ({ tile }) => (
  <div className={styles.tile}>
    <b className={styles.tileFigure}>{tile.figure}</b>
    <span className={styles.tileSub}>{tile.sub}</span>
  </div>
)

/** Clicks inside the fresh card keep the menu open (the identity slot closes it on any click) —
 *  except a pick in the owner picker, which navigates and should close it. */
function keepMenuOpen(e: React.MouseEvent): void {
  const target = e.target as Element | null
  if (!target?.closest?.('[role="option"], [role="group"] button')) {
    e.stopPropagation()
  }
}

/** Board 2a: the blue card — the expected first-week reach, the award, the owner picker in place
 *  (as the Stats tab's and the You card's "Leave an owner review"). */
const FreshMenuCard: React.VFC<{
  answer: MenuStatsResponse
  standing: CommunityStanding | null
}> = ({ answer, standing }) => {
  const [picking, setPicking] = useState(false)
  const card = freshCard(answer.stats, standing, answer.points?.review ?? null)
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={styles.fresh} onClick={keepMenuOpen}>
      {card.reach && <p className={styles.freshReach}>{card.reach}</p>}
      {card.award && <p className={styles.freshAward}>{card.award}</p>}
      {picking ? (
        <OwnPicker id="menu-own-results" autoFocus inline compact />
      ) : (
        <button
          type="button"
          className={clsx(base.btn, styles.freshCta)}
          onClick={() => setPicking(true)}
        >
          {'Leave an owner review →'}
        </button>
      )}
    </div>
  )
}

export interface AccountMenuStatsViewProps {
  state: MenuStatsState
  /** The reader's `/me` standing — the award's tier, the closest-badge pins. */
  standing: CommunityStanding | null
  /** `/me`'s published count: only picks the placeholder's shape while the read is on its way. */
  reviews: number
}

/**
 * The avatar menu's stats glance (Amendment B.2, boards 2a–2c) under the v1 head: the fresh card
 * without a counted review, else the grey block — readers this month with today's and the 14-day
 * sparkline, reading time (B.1) and rank tiles, one fact — and the "Up next" row. Pure: the SSR
 * tests render every state.
 */
export const AccountMenuStatsView: React.VFC<AccountMenuStatsViewProps> = ({
  state,
  standing,
  reviews,
}) => {
  if (state === 'loading') {
    // Hold the block's place (no jump when it lands): the grey block's height for a reader with
    // a published review, the fresh card's for one without.
    return (
      <div
        className={clsx(
          styles.statsPending,
          reviews > 0 ? styles.pendingData : styles.pendingFresh
        )}
        aria-busy="true"
        aria-label="Loading your stats"
      />
    )
  }
  if (!state) {
    return null
  }
  const { stats } = state
  if (menuIsFresh(stats)) {
    return (
      <div className={styles.glance}>
        <FreshMenuCard answer={state} standing={standing} />
      </div>
    )
  }
  const head = readersHead(stats)
  const spark = sparkBars(stats.readers.days)
  const fact = factLine(stats)
  const next = upNext(state, standing)
  return (
    <div className={styles.glance}>
      <div className={styles.block}>
        <div className={styles.readers}>
          <div className={styles.readersText}>
            <span className={styles.kicker}>{head.label}</span>
            <span className={styles.readersRow}>
              <b className={styles.readersFigure}>{head.figure}</b>
              {head.today && <span className={styles.today}>{head.today}</span>}
            </span>
          </div>
          <div
            className={styles.spark}
            role="img"
            aria-label={`Readers per day, last ${spark.length} days`}
          >
            {spark.map(bar => (
              <span
                key={bar.day}
                className={clsx(styles.sparkBar, {
                  [styles.sparkZero]: bar.tone === 'zero',
                  [styles.sparkToday]: bar.tone === 'today',
                })}
                style={{ height: `${bar.px}px` }}
              />
            ))}
          </div>
        </div>
        <div className={styles.tiles}>
          <MenuTile tile={readingTile(stats)} />
          <MenuTile tile={rankTile(stats)} />
        </div>
        {fact && <p className={styles.fact}>{fact}</p>}
      </div>
      {next && (
        <div className={styles.next}>
          <span className={styles.nextText}>
            <span className={styles.nextKicker}>{'Up next'}</span>
            <span className={styles.nextLabel}>{next.label}</span>
          </span>
          {next.pts && <span className={styles.nextPts}>{next.pts}</span>}
        </div>
      )}
    </div>
  )
}

/** The glance for the reader's own `handle`, read when the menu opens. */
const AccountMenuStats: React.VFC<{
  handle: string
  opened: number
  standing: CommunityStanding | null
  reviews: number
}> = ({ handle, opened, standing, reviews }) => (
  <AccountMenuStatsView
    state={useMenuStats(handle, opened)}
    standing={standing}
    reviews={reviews}
  />
)

export default AccountMenuStats

/** "NEW" beside the menu's Stats entry until this browser has opened a Stats tab
 *  (`sg_stats_seen`). Read after mount and on every open, never during render (no storage on
 *  the server; a tab opened since the last open hides it). */
export const StatsNewPill: React.VFC<{ opened: number }> = ({ opened }) => {
  const [show, setShow] = useState(false)
  useEffect(() => {
    setShow(!statsSeen())
  }, [opened])
  return show ? <span className={styles.newPill}>{'NEW'}</span> : null
}

/** The Stats entry's bar-chart icon (design 2a–2c), drawn in the row's text colour. */
export const StatsIcon: React.VFC<React.SVGAttributes<SVGElement>> = props => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </svg>
)
